import { useNavigate } from 'react-router-dom';

import { formatCnpj, formatRelativeDate, type Lead } from '../../../features/leads/index.js';

import { LeadStageBadge } from './LeadStageBadge.js';

/**
 * Tabela densa de leads para viewport desktop (≥sm).
 *
 * Padrão de **coluna combinada "Lead"** (nome em destaque + clínica
 * embaixo em muted) — mesmo visual do card mobile, consistência
 * cross-breakpoint. Decisão de design 2026-04-25 (Alexandre): o
 * formato anterior com Nome e Clínica em colunas separadas espremia
 * largura e quebrava texto em 2 linhas.
 *
 * Colunas:
 *   - **Lead** (nome + clínica) — sempre visível
 *   - **Email** — sempre visível, com truncate + tooltip nativo
 *   - **CNPJ** — só `xl:table-cell` (≥1280px); em laptops comuns
 *     fica oculto. CNPJ raramente é usado pela Karla na lista; está
 *     disponível na detail page.
 *   - **Estado** — badge `compact` (label curto preservando peso
 *     visual de milestone)
 *   - **Criado** — só `lg:table-cell` (≥1024px), `whitespace-nowrap`
 *
 * **Linha clicável**: `<tr>` com `role="link"`, `tabIndex={0}`,
 * `onClick` e `onKeyDown` (Enter/Space). Tap-target maior +
 * acessível por teclado. Sem `<Link>` aninhado dentro da row pra
 * evitar anti-pattern de link nested (a linha inteira é a navegação).
 *
 * Extraído de `LeadsListPage.tsx` em PR-M1-02-bis pra respeitar
 * §5.1 do AGENTS.md (hard limit 300 linhas/componente).
 */
export interface LeadsTableProps {
  leads: Lead[];
}

export function LeadsTable({ leads }: LeadsTableProps) {
  const navigate = useNavigate();

  const goToLead = (id: string) => navigate(`/superadmin/leads/${id}`);

  return (
    <div className="hidden overflow-x-auto sm:block">
      <table className="w-full text-sm">
        <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="py-3 pr-3 font-semibold">Lead</th>
            <th className="py-3 pr-3 font-semibold">Email</th>
            <th className="hidden py-3 pr-3 font-semibold xl:table-cell">CNPJ</th>
            <th className="py-3 pr-3 font-semibold">Estado</th>
            <th className="hidden py-3 font-semibold lg:table-cell">Criado</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr
              key={lead.id}
              className="cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-muted/40"
              onClick={() => goToLead(lead.id)}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToLead(lead.id);
                }
              }}
              aria-label={`Abrir lead ${lead.full_name}`}
            >
              <td className="py-3 pr-3">
                {/* Nome em destaque + clínica embaixo (espelha card mobile).
                    Sem <Link> aninhado — a row inteira é o "link" via
                    role+onClick (evita anti-pattern de link nested). */}
                <p className="font-medium text-foreground">{lead.full_name}</p>
                {lead.clinic_name_guess ? (
                  <p className="truncate text-sm text-muted-foreground">{lead.clinic_name_guess}</p>
                ) : null}
              </td>
              <td
                className="max-w-[18rem] truncate py-3 pr-3 text-muted-foreground"
                title={lead.email}
              >
                {lead.email}
              </td>
              <td className="hidden whitespace-nowrap py-3 pr-3 font-mono text-sm text-muted-foreground xl:table-cell">
                {lead.cnpj ? formatCnpj(lead.cnpj) : '—'}
              </td>
              <td className="py-3 pr-3">
                <LeadStageBadge stage={lead.stage} compact />
              </td>
              {/* Data relativa ("há 7 dias") em vez de absoluta — mesmo
                  padrão do card mobile (consistência cross-breakpoint) e
                  mais informativo no contexto de funil ("quão fresco
                  está esse lead?"). Decidido com Alexandre 2026-04-25.
                  Title attr expõe data absoluta no hover pra audit. */}
              <td
                className="hidden whitespace-nowrap py-3 text-muted-foreground lg:table-cell"
                title={new Date(lead.created_at).toLocaleString('pt-BR', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              >
                {formatRelativeDate(lead.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
